// tslint:disable:max-line-length
import {
    TCI_MARKETPLACE_LISTING_VIEW_MODE,
    TCI_MARKETPLACE_LISTING_TYPE,
    TCITenantAppType
} from "@tci/common";
import {Decision, BoolTable, Truth} from "booltable";
import {DecisionMonad, BoolTableMonad} from "booltable/dist/types";
import {
    CONDITION_IS_ALLOWED_IN,
    CONDITION_STATUS_MANAGING,
    CONDITION_STATUS_CONSUMING,
    MKP_MAIN_ITEMS,
    IMkpV2ListContext,
    IMkpV2ListEvalRes,
    formatResult,
    isAdminOfSameOrg,
    isOrgUserNotRO,
    isRO,
    ConditionsForStatusManaging,
    ConditionsForStatusConsuming
} from "./listing-bt-evaluations";

//#region - Conditions catalog binding condition string with the actual evaluation
const ConditionsForAllowed = (context: IMkpV2ListContext): BoolTableMonad =>
    BoolTable.of([
        [
            CONDITION_IS_ALLOWED_IN.LIST_CARD,
            Truth.of([
                [
                    MKP_MAIN_ITEMS.ICON,
                    MKP_MAIN_ITEMS.VERSION,
                    MKP_MAIN_ITEMS.DATE_CERTIFIED,
                    MKP_MAIN_ITEMS.DATE_UPDATED,
                    MKP_MAIN_ITEMS.PROVIDER_NAME
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.DETAIL,
            Truth.of([
                [
                    MKP_MAIN_ITEMS.ICON,
                    MKP_MAIN_ITEMS.VERSION,
                    MKP_MAIN_ITEMS.DATE_CERTIFIED,
                    MKP_MAIN_ITEMS.DATE_UPDATED,
                    MKP_MAIN_ITEMS.NEW_UPDATE,
                    MKP_MAIN_ITEMS.TAGS,
                    MKP_MAIN_ITEMS.CATEGORY_ICON,
                    MKP_MAIN_ITEMS.RATING_EDIT,
                    MKP_MAIN_ITEMS.MEDIA,
                    MKP_MAIN_ITEMS.HM_OPTIONS,
                    MKP_MAIN_ITEMS.HM_EDIT_DETAILS,
                    MKP_MAIN_ITEMS.HM_EDIT_UPDATE_VISIBILITY_PUBLIC,
                    MKP_MAIN_ITEMS.HM_EDIT_UPDATE_VISIBILITY_PRIVATE,
                    MKP_MAIN_ITEMS.HM_MANAGE_ACCESS,
                    MKP_MAIN_ITEMS.HM_WITHDRAW_PENDING_LISTING,
                    MKP_MAIN_ITEMS.HM_DELETE_LISTING
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.PREVIEW_CARD,
            Truth.of([
                [
                    MKP_MAIN_ITEMS.ICON,
                    MKP_MAIN_ITEMS.VERSION,
                    MKP_MAIN_ITEMS.DATE_CERTIFIED,
                    MKP_MAIN_ITEMS.DATE_UPDATED,
                    MKP_MAIN_ITEMS.PROVIDER_NAME,
                    MKP_MAIN_ITEMS.RATING_EDIT
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.PREVIEW_DETAIL,
            Truth.of([
                [
                    MKP_MAIN_ITEMS.ICON,
                    MKP_MAIN_ITEMS.VERSION,
                    MKP_MAIN_ITEMS.DATE_CERTIFIED,
                    MKP_MAIN_ITEMS.DATE_UPDATED,
                    MKP_MAIN_ITEMS.CATEGORY_ICON,
                    MKP_MAIN_ITEMS.RATING_EDIT,
                    MKP_MAIN_ITEMS.MEDIA
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ]
    ]);
//#endregion

// Use this function to see if an item is allowed in a certain mode
const CommonAllowanceCheck = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    let condTable: DecisionMonad;
    switch (context.mode) {
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.LIST_CARD), {visible: true}]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_DETAIL:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.DETAIL), {visible: true}]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.PREVIEW_CARD), {visible: true}]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.PREVIEW_DETAIL), {visible: true}]
            ]);
            break;
    }

    return condTable.run("any").chain(formatResult);
};

// Use this function to see if context is in preview mode
const isPreviewMode = (context: IMkpV2ListContext): boolean => {
    return [
        TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD,
        TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL
    ].includes(context.mode as any);
};

//#region Regular checks
export const CheckIcon = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    return allowanceResult;
};

export const CheckVersion = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    return allowanceResult;
};

export const CheckTags = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), {visible: true}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), {visible: true}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), {visible: true}]
        ]);

        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckNewUpdate = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && context.asset.hasUpgrade;
    return allowanceResult;
};

export const CheckDateCertified = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && context.asset.lastCertifiedDate > 0;

    if (allowanceResult.visible) { // If visible by mode then check by PREVIEW mode and status
        if (isPreviewMode(context)) { // Cases for PREVIEW mode
            allowanceResult.visible = context.mode === TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL; // PREVIEW_DETAIL is enabled
        } else {
            // Always visible when is public and greater than 0
            allowanceResult.visible = context.asset.lastCertifiedDate > 0;
        }
    }
    return allowanceResult;
};

export const CheckDateUpdated = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // If visible by mode then check by status managing
    if (allowanceResult.visible && !isPreviewMode(context)) {
        // If card only if no certified date
        if (context.mode === TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD) {
            allowanceResult.visible = context.asset.lastCertifiedDate === 0;
        }
    }
    return allowanceResult;
};

export const CheckProviderName = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    return CommonAllowanceCheck(context);
};

export const CheckCategoryIcon = (context: IMkpV2ListContext, allowanceCheck = CommonAllowanceCheck): IMkpV2ListEvalRes => {
    const allowanceResult = allowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && !context.asset.tenantType.equals(TCITenantAppType.unknown);
    return allowanceResult;
};

/**
 * For "Ratings" all the ealuations reside under the "consuming" cycle
 * @param context
 */
export const CheckRatings = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    if (allowanceResult.visible) { // If visible by mode then check by PREVIEW mode and status
        if (isPreviewMode(context)) { // Cases for PREVIEW mode
            allowanceResult.enabled = false;
        } else {
            const condTable: DecisionMonad = Decision.of([
                [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), {visible: true, enabled: false}],
                [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), {visible: true, enabled: false}],
                [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), {visible: true, enabled: false}],

                // IPAS-17127 - RO can NOT rate
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_APPROVED), {visible: true, enabled: !isRO(context)}],
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_DISABLED), {visible: true, enabled: !isRO(context)}],
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_ENABLED), {visible: true, enabled: !isRO(context)}],
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_GET), {visible: true, enabled: !isRO(context)}],

                // Disable for the following states
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_REQUEST), {visible: true, enabled: false}],
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_PENDING_REQUEST), {visible: true, enabled: false}],
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_REJECTED), {visible: true, enabled: false}],
                [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_ACCESS_REVOKED), {visible: true, enabled: false}]
            ]);
            return condTable.run("any").chain(formatResult);
        }
    }
    return allowanceResult;
};

export const CheckMedia = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    return CommonAllowanceCheck(context);
};
//#endregion

//#region HM checks
const CheckHM = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && isOrgUserNotRO(context);

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), {visible: true, enabled: true}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), {visible: true, enabled: true}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), {visible: true, enabled: true}]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

const CheckHMEdit = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && isOrgUserNotRO(context);

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), {visible: true, enabled: isAdminOfSameOrg(context)}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), {visible: true, enabled: isOrgUserNotRO(context)}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), {visible: true, enabled: false}]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

const CheckVisibilityPublic = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && isOrgUserNotRO(context); // IPAS-17106 - Not for OOTB

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), {visible: true, enabled: isAdminOfSameOrg(context)}]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

const CheckVisibilityPrivate = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && isOrgUserNotRO(context);

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), {visible: true, enabled: isAdminOfSameOrg(context)}]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

const CheckAccess = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible &&
        isOrgUserNotRO(context) &&
        context.asset.listingType !== TCI_MARKETPLACE_LISTING_TYPE.Accelerator; // IPAS-17144 - Not for Accelerator

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), {visible: true, enabled: isAdminOfSameOrg(context)}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), {visible: true, enabled: isAdminOfSameOrg(context)}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), {visible: true, enabled: isAdminOfSameOrg(context)}]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

const CheckWithdraw = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && isOrgUserNotRO(context);

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), {visible: true, enabled: false}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), {visible: true, enabled: false}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), {visible: true, enabled: isAdminOfSameOrg(context)}]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

const CheckDelete = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);
    allowanceResult.visible = allowanceResult.visible && isAdminOfSameOrg(context);

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), {visible: true, enabled: context.asset.deletable}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), {visible: true, enabled: context.asset.deletable}],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), {visible: true, enabled: context.asset.deletable}]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};
//#endregion

export const CheckOverMainItem = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    switch (context.item) {
        case MKP_MAIN_ITEMS.ICON:
            return CheckIcon(context);
        case MKP_MAIN_ITEMS.VERSION:
            return CheckVersion(context);
        case MKP_MAIN_ITEMS.TAGS:
            return CheckTags(context);
        case MKP_MAIN_ITEMS.NEW_UPDATE:
            return CheckNewUpdate(context);
        case MKP_MAIN_ITEMS.DATE_CERTIFIED:
            return CheckDateCertified(context);
        case MKP_MAIN_ITEMS.DATE_UPDATED:
            return CheckDateUpdated(context);
        case MKP_MAIN_ITEMS.PROVIDER_NAME:
            return CheckProviderName(context);
        case MKP_MAIN_ITEMS.CATEGORY_ICON:
            return CheckCategoryIcon(context);
        case MKP_MAIN_ITEMS.RATING_EDIT:
            return CheckRatings(context);
        case MKP_MAIN_ITEMS.MEDIA:
            return CheckMedia(context);

        case MKP_MAIN_ITEMS.HM_OPTIONS:
            return CheckHM(context);
        case MKP_MAIN_ITEMS.HM_EDIT_DETAILS:
            return CheckHMEdit(context);
        case MKP_MAIN_ITEMS.HM_EDIT_UPDATE_VISIBILITY_PUBLIC:
            return CheckVisibilityPublic(context);
        case MKP_MAIN_ITEMS.HM_EDIT_UPDATE_VISIBILITY_PRIVATE:
            return CheckVisibilityPrivate(context);
        case MKP_MAIN_ITEMS.HM_MANAGE_ACCESS:
            return CheckAccess(context);
        case MKP_MAIN_ITEMS.HM_WITHDRAW_PENDING_LISTING:
            return CheckWithdraw(context);
        case MKP_MAIN_ITEMS.HM_DELETE_LISTING:
            return CheckDelete(context);
    }
};
